// src/services/milestones.js
//
// ── Milestone badges: the single source of truth ──────────────────────────
//
// Badges are DERIVED, not stored. Everything here is computed from the
// completions array the Dashboard already fetches, so the personal badge
// shelf costs ZERO additional Firestore reads.
//
// Two families:
//   • count      — N qualifying classes completed (1/10/25/50/100/250)
//   • consistency — ≥1 qualifying class per week for N consecutive weeks
//   • seasonal    — N qualifying classes inside a fixed date window (one-off
//                   holiday badges, e.g. The Gift of Showing Up 2026)
//
// See docs/decisions/adr-milestone-badges.md.

import { trainerDayKey } from '../utils/timezone';

// ── What counts as a "class" ─────────────────────────────────────────────
//
// Product decision (2026-08-24): badges count CLASSES and on-demand workouts
// only — never free shorts, program videos, or Series days.
//
// Completion records written since the 2026-06-06 event-log overhaul carry a
// `source`, which answers this directly. Older records do not, and a manual
// log from the On Demand tab was historically written IDENTICALLY to one from
// the Shorts tab (`source:'manual'`, `programId:null`) — so for legacy docs we
// infer from the videoId shape:
//
//   • class recordings are keyed by Mux asset id  → long, no dashes
//   • Bunny videos (shorts, program videos)       → UUID, 8-4-4-4-12 with dashes
//
// This is an INFERENCE and is labelled as such wherever a number derived from
// it is shown. A one-time backfill stamping `source` on legacy docs would make
// it exact; see the ADR. Chosen over excluding legacy records, which would rob
// the longest-tenured members of credit they genuinely earned.
const BUNNY_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isClassCompletion(c) {
    if (!c) return false;
    // A programId ALWAYS means program or series scope — never a class.
    if (c.programId) return false;

    switch (c.source) {
        case 'live':
        case 'ondemand':
            return true;
        case 'program':
        case 'series':
            return false;
        case 'manual':
        default:
            // Ambiguous (legacy, or a manual log that didn't record surface).
            // Fall back to the id-shape inference described above.
            return !BUNNY_ID_RE.test(String(c.videoId || ''));
    }
}

// ── Week bucketing ───────────────────────────────────────────────────────
// Sunday-start weeks in America/Chicago, matching computeStats' existing
// convention so a member's streak and their badges can never disagree.
export function weekKeyOf(dayKey) {
    if (!dayKey) return null;
    const [y, m, d] = dayKey.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d, 12));
    dt.setUTCDate(dt.getUTCDate() - dt.getUTCDay()); // back to Sunday
    return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
}

function addWeeks(weekKey, n) {
    const [y, m, d] = weekKey.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d, 12));
    dt.setUTCDate(dt.getUTCDate() + n * 7);
    return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
}

// ── Badge catalogue ──────────────────────────────────────────────────────
// `tier` drives the Tonal Seal ramp (see MilestoneBadge.js) — 0 lightest,
// 5 = gold. Order here is the order they appear on the shelf.
export const BADGES = [
    { key: 'first',    family: 'count',       threshold: 1,   tier: 0, name: 'First Step',        requirement: '1 class completed' },
    { key: 'ten',      family: 'count',       threshold: 10,  tier: 1, name: '10 Classes Strong', requirement: '10 classes' },
    { key: 'tfive',    family: 'count',       threshold: 25,  tier: 2, name: '25 Class Club',     requirement: '25 classes' },
    { key: 'fifty',    family: 'count',       threshold: 50,  tier: 3, name: '50 Class Club',     requirement: '50 classes' },
    { key: 'hundred',  family: 'count',       threshold: 100, tier: 4, name: '100 Class Club',    requirement: '100 classes' },
    { key: 'quarter',  family: 'count',       threshold: 250, tier: 5, name: '250 Class Club',    requirement: '250 classes' },
    { key: 'mo1',      family: 'consistency', threshold: 4,   tier: 2, name: '1 Month Strong',    requirement: '1 class/week for 4 weeks' },
    { key: 'mo3',      family: 'consistency', threshold: 12,  tier: 3, name: '3 Months Strong',   requirement: '1 class/week for 12 weeks' },
    { key: 'mo6',      family: 'consistency', threshold: 26,  tier: 5, name: '6 Months Strong',   requirement: '1 class/week for 26 weeks' },
    // Seasonal (one-off, 2026-11-01). 18 classes between Nov 1 and Dec 31 2026,
    // America/Chicago days (same bucket as streaks and every other badge).
    // VISIBILITY RULE (owner decision): shown locked-with-progress during the
    // season; once the window closes it disappears for anyone who didn't earn
    // it, and is kept forever by anyone who did. See isBadgeVisible().
    // `api/admin-backfill-milestones.js` carries a copy of this list — change
    // both together.
    { key: 'gift26',   family: 'seasonal',    threshold: 18,  tier: 5, name: 'The Gift of Showing Up', requirement: '18 classes, Nov 1 – Dec 31, 2026', windowStart: '2026-11-01', windowEnd: '2026-12-31' },
];

export const BADGE_BY_KEY = BADGES.reduce((acc, b) => { acc[b.key] = b; return acc; }, {});

// ── The computation ──────────────────────────────────────────────────────
//
// Returns:
//   {
//     classCount,                       // qualifying completions, all time
//     earned:   [{ ...badge, earnedAt, earnedDayCT }],   // oldest first
//     unearned: [{ ...badge, progress, current, target }],
//     next,                             // the closest unearned badge (or null)
//     currentWeekRun                    // consecutive-week run, for display
//   }
//
// earnedAt is the TRUE date the badge was reached — the completedAt of the
// Nth qualifying class, or the end of the Nth consecutive week. This is what
// lets the Milestone Wall seed with real history rather than a wall of
// backdated "just now" entries.
export function computeMilestones(completions) {
    const list = (Array.isArray(completions) ? completions : []).filter(isClassCompletion);

    // Oldest → newest. Legacy docs derive their CT day from completedAt.
    const sorted = list
        .map((c) => ({
            at: c.completedAt || null,
            dayKey: c.completedDayCT || (c.completedAt ? trainerDayKey(c.completedAt) : null),
        }))
        .filter((c) => c.dayKey)
        .sort((a, b) => (a.dayKey < b.dayKey ? -1 : a.dayKey > b.dayKey ? 1 : 0));

    const classCount = sorted.length;

    // ── Consecutive-week runs ──
    // Only COMPLETED weeks count toward a consistency badge, so a badge can
    // never flicker in and out mid-week. The current (partial) week is tracked
    // separately for the progress line.
    const activeWeeks = Array.from(new Set(sorted.map((c) => weekKeyOf(c.dayKey)))).sort();
    const thisWeek = weekKeyOf(trainerDayKey(new Date().toISOString()));

    // Longest run of consecutive weeks, and the CT day each run-length was
    // first reached (used as the badge's earned date).
    const runReachedAt = {};   // runLength -> weekKey in which it was reached
    let bestRun = 0;
    let run = 0;
    let prev = null;
    activeWeeks.forEach((wk) => {
        run = (prev && addWeeks(prev, 1) === wk) ? run + 1 : 1;
        prev = wk;
        if (!runReachedAt[run]) runReachedAt[run] = wk;
        if (run > bestRun) bestRun = run;
    });

    // Run counting only completed weeks — the in-progress week doesn't count
    // toward earning, but it also must not BREAK a run that's still alive.
    const completedWeeks = activeWeeks.filter((wk) => wk < thisWeek);
    let completedRun = 0, cprev = null, bestCompletedRun = 0;
    completedWeeks.forEach((wk) => {
        completedRun = (cprev && addWeeks(cprev, 1) === wk) ? completedRun + 1 : 1;
        cprev = wk;
        if (completedRun > bestCompletedRun) bestCompletedRun = completedRun;
    });

    // ── Current run (PROGRESS ONLY — never used to decide earning) ──
    // Consecutive completed weeks ending LAST week. Earning stays on
    // bestCompletedRun (any run in history counts, badges are permanent), but
    // the progress a member sees toward an UNEARNED weekly badge must be the
    // run she's on now — "2 of 4" reads as "you're 2 weeks in", and a stale
    // personal best that won't move when she shows up reads as broken
    // (member report 2026-10-05). The in-progress week is excluded for the
    // same reason it's excluded from earning: progress can never claim
    // "4 of 4" for a badge that isn't awarded yet.
    const completedSet = new Set(completedWeeks);
    let currentRun = 0;
    for (let wk = addWeeks(thisWeek, -1); completedSet.has(wk); wk = addWeeks(wk, -1)) {
        currentRun += 1;
    }

    // Display only (not read by any UI today). Previously assigned bestRun,
    // contradicting this comment.
    const currentWeekRun = currentRun;

    const earned = [];
    const unearned = [];

    BADGES.forEach((b) => {
        if (b.family === 'count') {
            if (classCount >= b.threshold) {
                const hit = sorted[b.threshold - 1];
                earned.push({ ...b, earnedAt: hit?.at || null, earnedDayCT: hit?.dayKey || null });
            } else {
                unearned.push({
                    ...b,
                    current: classCount,
                    target: b.threshold,
                    progress: b.threshold ? Math.min(1, classCount / b.threshold) : 0,
                });
            }
        } else if (b.family === 'seasonal') {
            const today = trainerDayKey(new Date().toISOString());
            const inWindow = sorted.filter((c) => c.dayKey >= b.windowStart && c.dayKey <= b.windowEnd);
            if (inWindow.length >= b.threshold) {
                const hit = inWindow[b.threshold - 1];
                earned.push({ ...b, earnedAt: hit?.at || null, earnedDayCT: hit?.dayKey || null });
            } else if (today >= b.windowStart && today <= b.windowEnd) {
                unearned.push({
                    ...b,
                    current: inWindow.length,
                    target: b.threshold,
                    progress: b.threshold ? Math.min(1, inWindow.length / b.threshold) : 0,
                });
            }
            // Outside the window and not earned ⇒ omitted entirely (hidden).
        } else {
            if (bestCompletedRun >= b.threshold) {
                // Earned at the END of the week that completed the run.
                const wk = runReachedAt[b.threshold];
                const endOfWeek = wk ? addWeeks(wk, 1) : null;
                earned.push({ ...b, earnedAt: null, earnedDayCT: endOfWeek });
            } else {
                // Progress = the CURRENT run; `best` lets the UI add a quiet
                // "your best" note so an earlier longer run isn't forgotten.
                unearned.push({
                    ...b,
                    current: currentRun,
                    best: bestCompletedRun,
                    target: b.threshold,
                    progress: b.threshold ? Math.min(1, currentRun / b.threshold) : 0,
                });
            }
        }
    });

    // Oldest earned first — the shelf reads as a journey.
    earned.sort((a, b) => String(a.earnedDayCT || '').localeCompare(String(b.earnedDayCT || '')));
    // Closest to earning first.
    unearned.sort((a, b) => b.progress - a.progress);

    // The badges this member can see, in catalogue order. Every permanent badge
    // is always here; a seasonal badge only while its window is open or once
    // earned. Drives the "X of N earned" count and detail-card browsing.
    const shown = new Set([...earned, ...unearned].map((b) => b.key));
    const visibleKeys = BADGES.filter((b) => shown.has(b.key)).map((b) => b.key);

    return {
        classCount,
        earned,
        unearned,
        next: unearned[0] || null,
        currentWeekRun,
        visibleKeys,
    };
}

// ── "Your best" note for weekly badges ───────────────────────────────────
// Returns the note text for an UNEARNED consistency badge when the member's
// best run beats her current one, else null. Shown only from a 2-week best
// up — "your best: 1 week" is noise, and it sidesteps singular copy.
// `{count}` in the admin template is replaced with the best run.
export function bestRunNote(badge, copy = {}) {
    if (!badge || badge.family !== 'consistency') return null;
    const best = Number(badge.best) || 0;
    if (best < 2 || best <= (Number(badge.current) || 0)) return null;
    const tpl = (copy && copy.milestonesBestRunTemplate) || 'Your best: {count} weeks';
    return tpl.replace(/\{count\}/g, String(best));
}

// ── Celebration bookkeeping ──────────────────────────────────────────────
//
// Phase 1 keeps this in localStorage — no Firestore collection, no writes.
// Its ONLY job is to stop a badge celebrating twice. A member who clears
// storage sees one repeat celebration; that is a strictly better failure
// than a write path on the Dashboard's critical render.
//
// Backdated badges (everything a member had already earned when this shipped)
// are seeded silently on first run, so nobody is ambushed by six popups.
const LS_KEY = 'mom_milestones_seen_v1';

function readSeen(userId) {
    if (!userId || typeof window === 'undefined') return null;
    try {
        const raw = window.localStorage.getItem(`${LS_KEY}:${userId}`);
        return raw ? JSON.parse(raw) : null;
    } catch (_) { return null; }
}

function writeSeen(userId, keys) {
    if (!userId || typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(`${LS_KEY}:${userId}`, JSON.stringify(keys));
    } catch (_) { /* private mode / quota — celebration is best-effort */ }
}

// Returns the badges newly earned SINCE the last visit, and records them.
// On a member's very first run this returns [] (silent seed) — see above.
export function claimNewlyEarned(userId, earned) {
    const keys = (earned || []).map((b) => b.key);
    const seen = readSeen(userId);

    if (seen === null) {
        writeSeen(userId, keys);
        return [];
    }

    const seenSet = new Set(seen);
    const fresh = (earned || []).filter((b) => !seenSet.has(b.key));
    if (fresh.length) writeSeen(userId, keys);
    return fresh;
}
